import { useCallback, useEffect, useRef, useState } from 'react';
import { AddressHelpers } from '../../../helpers';
import { StringUtil } from '../../../utils';
import {
  ADDRESS_EDIT_RESET_DEBOUNCE_MS,
  EMPTY_COLLECTION,
  EMPTY_DELIVERY,
} from '../constants';

const SCHEDULE_INVALIDATING_FIELDS = new Set(['postcode', 'street', 'town']);

function patchTouchesScheduleInvalidatingField(patch) {
  return Object.keys(patch).some(key => SCHEDULE_INVALIDATING_FIELDS.has(key));
}

function deriveSliceFromAfd(prev, fullAddress) {
  if (!fullAddress?.Key) {
    return {
      next: {
        ...prev,
        addressId: '',
        postcode: '',
        town: '',
        county: '',
        organisation: '',
        street: '',
        locality: '',
        _coordinates: null,
      },
      changed: Boolean(prev.addressId) || Boolean(prev.postcode),
    };
  }
  const { addressLine1, addressLine2 } = AddressHelpers.transformAddressValues({
    street: fullAddress.Street,
    locality: fullAddress.Locality,
    property: fullAddress.Property,
  });
  const postcode = fullAddress.Postcode
    ? StringUtil.removeWhitespacesAndSymbols(fullAddress.Postcode)
    : '';
  return {
    next: {
      ...prev,
      addressId: fullAddress.Key,
      postcode,
      town: fullAddress.Town || '',
      county: fullAddress.PostalCounty || '',
      organisation: fullAddress.Organisation || '',
      street: addressLine1 || '',
      locality: addressLine2 || '',
      _coordinates: AddressHelpers.formatCoordinates(fullAddress),
    },
    changed: String(fullAddress.Key) !== String(prev.addressId),
  };
}

// eslint-disable-next-line max-lines-per-function -- small hook
export function useCitysprintAddressEditor({ initial, onAddressChanged }) {
  const [delivery, setDelivery] = useState(
    () => initial?.delivery ?? { ...EMPTY_DELIVERY }
  );
  const [collection, setCollection] = useState(
    () => initial?.collection ?? { ...EMPTY_COLLECTION }
  );

  const deliveryRef = useRef(delivery);
  const collectionRef = useRef(collection);
  useEffect(() => {
    deliveryRef.current = delivery;
    collectionRef.current = collection;
  }, [delivery, collection]);

  const onAddressChangedRef = useRef(onAddressChanged);
  onAddressChangedRef.current = onAddressChanged;

  const debounceTimerRef = useRef(null);
  useEffect(
    () => () => {
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    },
    []
  );

  const fireImmediate = useCallback(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    onAddressChangedRef.current?.({ immediate: true });
  }, []);

  const fireDebounced = useCallback(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onAddressChangedRef.current?.({ immediate: false });
    }, ADDRESS_EDIT_RESET_DEBOUNCE_MS);
  }, []);

  const handlePatch = useCallback(
    patch => {
      if (!patch || typeof patch !== 'object') {
        return;
      }
      if (!patchTouchesScheduleInvalidatingField(patch)) {
        return;
      }
      const postcodeEdited = Object.prototype.hasOwnProperty.call(
        patch,
        'postcode'
      );
      if (postcodeEdited) {
        fireImmediate();
      } else {
        fireDebounced();
      }
    },
    [fireImmediate, fireDebounced]
  );

  const patchDelivery = useCallback(
    patch => {
      setDelivery(prev => ({
        ...prev,
        ...patch,
        ...(patchTouchesScheduleInvalidatingField(patch)
          ? { _coordinates: null }
          : {}),
      }));
      handlePatch(patch);
    },
    [handlePatch]
  );

  const patchCollection = useCallback(
    patch => {
      setCollection(prev => ({
        ...prev,
        ...patch,
        ...(patchTouchesScheduleInvalidatingField(patch)
          ? { _coordinates: null }
          : {}),
      }));
      handlePatch(patch);
    },
    [handlePatch]
  );

  const applyAfdToDelivery = useCallback(
    fullAddress => {
      if (!fullAddress?.Key) {
        return;
      }
      const prev = deliveryRef.current;
      if (
        String(fullAddress.Key) === String(prev.addressId) &&
        !fullAddress.isOptionSelectedManually
      ) {
        if (!prev._coordinates) {
          const coords = AddressHelpers.formatCoordinates(fullAddress);
          if (coords) {
            setDelivery(current => ({ ...current, _coordinates: coords }));
          }
        }
        return;
      }
      const { next, changed } = deriveSliceFromAfd(prev, fullAddress);
      setDelivery(next);
      if (changed) {
        fireImmediate();
      }
    },
    [fireImmediate]
  );

  const applyAfdToCollection = useCallback(
    fullAddress => {
      if (!fullAddress?.Key) {
        return;
      }
      const prev = collectionRef.current;
      if (
        String(fullAddress.Key) === String(prev.addressId) &&
        !fullAddress.isOptionSelectedManually
      ) {
        if (!prev._coordinates) {
          const coords = AddressHelpers.formatCoordinates(fullAddress);
          if (coords) {
            setCollection(current => ({ ...current, _coordinates: coords }));
          }
        }
        return;
      }
      const { next, changed } = deriveSliceFromAfd(prev, fullAddress);
      setCollection(next);
      if (changed) {
        fireImmediate();
      }
    },
    [fireImmediate]
  );

  const resetAddresses = useCallback(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setDelivery({ ...EMPTY_DELIVERY });
    setCollection({ ...EMPTY_COLLECTION });
  }, []);

  return {
    delivery,
    collection,
    patchDelivery,
    patchCollection,
    applyAfdToDelivery,
    applyAfdToCollection,
    resetAddresses,
  };
}
